import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar, 
  MapPin, 
  Users, 
  Clock, 
  XCircle,
  CreditCard,
  CheckCircle2,
  Music,
  Star,
  Ticket,
  Mail,
  Phone,
  ArrowLeft,
  Beer
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import MajofestForm from '../components/MajofestForm';
import { 
  festivalInfo, 
  programSchedule, 
  whyJoinReasons, 
  vendorCategories, 
  importantRules, 
  pricingConditions,
  programHighlights,
  contactInfo,
  heroImage
} from '../data/majofestData';

const MajofestLanding = () => {
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);

  const scrollToForm = () => {
    setShowForm(true);
    setTimeout(() => {
      document.getElementById('application-form')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const iconComponents = {
    Users, Clock, Calendar, CheckCircle2, XCircle, CreditCard, MapPin, Star, Ticket, Music, Beer
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-900 via-teal-800 to-teal-900">
      {/* Header */}
      <header className="fixed top-0 w-full bg-gradient-to-r from-teal-600 to-amber-500 z-50 shadow-2xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-white hover:text-amber-200 font-black"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Přehled akcí</span>
          </Button>
          <Button 
            onClick={scrollToForm}
            className="bg-white hover:bg-amber-100 text-teal-700 font-black px-6 py-2 rounded-full shadow-lg hover:shadow-xl transition-all"
          >
            PŘIHLÁSIT SE
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 text-white overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src={heroImage}
            alt="Májofest Otrokovice"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-teal-900 via-teal-800 to-amber-800 opacity-95"></div>
        </div>
        
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <div className="inline-block bg-amber-500 text-teal-900 px-8 py-3 rounded-full font-black text-xl mb-6 shadow-2xl">
            {festivalInfo.edition}
          </div>
          
          <div className="mb-8">
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-black mb-4 leading-tight">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-teal-300">
                MÁJOFEST
              </span><br/>
              <span className="text-white text-4xl md:text-5xl">
                OTROKOVICE
              </span>
            </h1>
            <p className="text-2xl md:text-3xl font-bold text-amber-300 mt-4">
              {festivalInfo.subtitle}
            </p>
          </div>

          <div className="inline-block bg-amber-500 text-teal-900 px-16 py-6 rounded-full font-black text-4xl md:text-5xl mb-8 shadow-2xl transform hover:scale-110 transition-transform">
            {festivalInfo.date}
          </div>
          
          <div className="text-2xl md:text-3xl mb-3 text-amber-300 font-bold">
            <MapPin className="inline h-8 w-8 mb-1" /> {festivalInfo.location}
          </div>

          <div className="text-2xl md:text-3xl mb-8 text-teal-300 font-black">
            <Clock className="inline h-8 w-8 mb-1" /> {festivalInfo.time}
          </div>

          <div className="inline-block bg-teal-600 text-white px-12 py-4 rounded-lg font-black text-2xl mb-10 shadow-2xl border-4 border-amber-400">
            🍺 {festivalInfo.entrance} 🎸
          </div>

          <div className="mt-10">
            <Button 
              onClick={scrollToForm}
              size="lg"
              className="bg-gradient-to-r from-amber-500 to-teal-600 hover:from-amber-600 hover:to-teal-700 text-white text-2xl px-20 py-10 font-black rounded-full shadow-2xl hover:shadow-3xl transition-all transform hover:scale-110 border-4 border-white"
            >
              🍺 PŘIHLÁSIT STÁNEK 🎸
            </Button>
          </div>

          <div className="mt-12 text-amber-300 text-2xl md:text-3xl font-black max-w-4xl mx-auto">
            Pivní speciály • Živá hudba • Skvělá atmosféra! 🎉
          </div>
        </div>
      </section>

      {/* Festival Stats */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-teal-800 to-amber-700 border-y-4 border-amber-400">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div className="bg-gradient-to-br from-amber-500 to-teal-600 text-white p-8 rounded-2xl shadow-2xl transform hover:scale-105 transition-transform">
              <Calendar className="mx-auto h-12 w-12 mb-4" />
              <div className="text-4xl font-black mb-2">{festivalInfo.date}</div>
              <div className="text-white/80 text-sm font-bold">Termín</div>
            </div>
            <div className="bg-gradient-to-br from-teal-600 to-amber-500 text-white p-8 rounded-2xl shadow-2xl transform hover:scale-105 transition-transform">
              <Clock className="mx-auto h-12 w-12 mb-4" />
              <div className="text-4xl font-black mb-2">{festivalInfo.time}</div>
              <div className="text-white/80 text-sm font-bold">Čas</div>
            </div>
            <div className="bg-gradient-to-br from-amber-500 to-teal-600 text-white p-8 rounded-2xl shadow-2xl transform hover:scale-105 transition-transform">
              <Users className="mx-auto h-12 w-12 mb-4" />
              <div className="text-4xl font-black mb-2">{festivalInfo.expectedVisitors}+</div>
              <div className="text-white/80 text-sm font-bold">Návštěvníků</div>
            </div>
            <div className="bg-gradient-to-br from-teal-600 to-amber-500 text-white p-8 rounded-2xl shadow-2xl transform hover:scale-105 transition-transform">
              <Beer className="mx-auto h-12 w-12 mb-4" />
              <div className="text-3xl font-black mb-2">20 druhů</div>
              <div className="text-white/80 text-sm font-bold">Piva na čepu</div>
            </div>
          </div>
        </div>
      </section>

      {/* Program Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-teal-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-5xl md:text-6xl font-black mb-4 text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-teal-300">
              🎸 Program festivalu
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              {programSchedule.friday.description}
            </p>
          </div>

          <div className="grid md:grid-cols-1 gap-8">
            <Card className="bg-gradient-to-br from-teal-800 to-teal-900 shadow-2xl border-4 border-amber-400">
              <CardHeader className="bg-gradient-to-r from-amber-500 to-teal-600 text-white">
                <CardTitle className="text-4xl font-black">{programSchedule.friday.day}</CardTitle>
                <CardDescription className="text-white/90 text-xl mt-2 font-bold">
                  {programSchedule.friday.time} | {programSchedule.friday.description}
                </CardDescription>
              </CardHeader>
              <CardContent className="p-8">
                <div className="grid md:grid-cols-2 gap-6">
                  {programHighlights.map((item, index) => (
                    <div key={index} className="flex items-start gap-3 p-4 bg-teal-700/50 rounded-lg border-2 border-amber-400/30 hover:border-amber-400 transition-colors">
                      <CheckCircle2 className="h-6 w-6 text-amber-400 flex-shrink-0 mt-1" />
                      <span className="font-bold text-lg text-white">{item}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Join Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-teal-800 to-teal-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-5xl md:text-6xl font-black mb-4 text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-teal-300">
              🍺 Proč se zúčastnit?
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Výhody pro prodejce jídla a grilovaných specialit
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyJoinReasons.map((reason, index) => {
              const IconComponent = iconComponents[reason.icon];
              return (
                <Card key={index} className="bg-gradient-to-br from-teal-800 to-teal-900 border-4 border-amber-400 hover:shadow-2xl hover:shadow-amber-400/50 transition-all transform hover:scale-105">
                  <CardHeader>
                    <div className="bg-gradient-to-r from-amber-500 to-teal-600 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                      <IconComponent className="h-8 w-8 text-white" />
                    </div>
                    <CardTitle className="text-2xl font-black text-amber-400">{reason.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-300 leading-relaxed">{reason.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Vendor Categories */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-teal-900 border-y-4 border-amber-400">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-5xl md:text-6xl font-black mb-4 text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-teal-300">
              Koho hledáme?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <Card className="bg-gradient-to-br from-amber-500 to-teal-600 text-white shadow-2xl border-4 border-white">
              <CardHeader>
                <CardTitle className="text-3xl font-black">{vendorCategories.priority.title}</CardTitle>
                <CardDescription className="text-white/90 text-lg mt-2 font-bold">
                  {vendorCategories.priority.description}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {vendorCategories.priority.requirements.map((req, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle2 className="h-6 w-6 flex-shrink-0 mt-1" />
                      <span className="font-bold">{req}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-teal-600 to-amber-500 text-white shadow-2xl border-4 border-white">
              <CardHeader>
                <CardTitle className="text-3xl font-black">{vendorCategories.additional.title}</CardTitle>
                <CardDescription className="text-white/90 text-lg mt-2 font-bold">
                  {vendorCategories.additional.description}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {vendorCategories.additional.categories.map((cat, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle2 className="h-6 w-6 flex-shrink-0 mt-1" />
                      <span className="font-bold">{cat}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-teal-800 to-teal-900">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-5xl md:text-6xl font-black mb-4 text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-teal-300">
              💰 Ceník
            </h2>
            <p className="text-xl text-gray-300">
              Transparentní ceny pro všechny prodejce
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <Card className="bg-gradient-to-br from-amber-500 to-teal-600 text-white shadow-2xl border-4 border-white">
              <CardHeader>
                <CardTitle className="text-2xl font-black text-white">{pricingConditions.grill.title}</CardTitle>
                <CardDescription className="text-white/90 text-base font-bold">{pricingConditions.grill.category}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-5xl font-black mb-2">{pricingConditions.grill.price}</div>
                <div className="text-white/90 text-base font-bold">Velikost: {pricingConditions.grill.size}</div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-teal-600 to-amber-500 text-white shadow-2xl border-4 border-white">
              <CardHeader>
                <CardTitle className="text-2xl font-black text-white">{pricingConditions.additional.title}</CardTitle>
                <CardDescription className="text-white/90 text-base font-bold">{pricingConditions.additional.category}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-5xl font-black mb-2">{pricingConditions.additional.price}</div>
                <div className="text-white/90 text-base font-bold">Velikost: {pricingConditions.additional.size}</div>
              </CardContent>
            </Card>
          </div>

          <div className="grid md:grid-cols-1 gap-6 mb-8">
            <Card className="bg-teal-800 shadow-2xl border-4 border-amber-400">
              <CardHeader>
                <CardTitle className="text-2xl font-black text-amber-400">{pricingConditions.electricity.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {pricingConditions.electricity.options.map((option, index) => (
                    <div key={index} className="flex justify-between items-center p-4 bg-teal-900/50 rounded-lg border-2 border-amber-400/30">
                      <span className="font-bold text-white text-lg">{option.range}</span>
                      <span className="text-2xl font-black text-amber-400">{option.price}</span>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-gray-400 mt-3 font-bold">{pricingConditions.electricity.note}</p>
              </CardContent>
            </Card>
          </div>

          <div className="text-center p-6 bg-gradient-to-r from-amber-500 to-teal-600 rounded-xl border-4 border-white">
            <p className="text-2xl font-black text-white">⚠️ {pricingConditions.note}</p>
          </div>

          <div className="mt-6 p-4 bg-green-900/50 border-l-4 border-green-500 rounded">
            <p className="text-white font-bold">
              <strong>✓ Bez vratné kauce</strong> - na Májofestu nevybíráme vratnou kauci
            </p>
          </div>
        </div>
      </section>

      {/* Important Rules */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-teal-800 to-amber-700 border-y-4 border-amber-400">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-5xl md:text-6xl font-black mb-4 text-white">
              ⚠️ Důležitá pravidla
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {importantRules.map((rule, index) => {
              const IconComponent = iconComponents[rule.icon];
              return (
                <Card key={index} className="bg-teal-900 shadow-2xl border-4 border-amber-400">
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <div className="bg-amber-500 w-12 h-12 rounded-full flex items-center justify-center">
                        <IconComponent className="h-6 w-6 text-teal-900" />
                      </div>
                      <CardTitle className="text-2xl font-black text-amber-400">{rule.title}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-white font-bold mb-2">{rule.description}</p>
                    {rule.note && (
                      <p className="text-sm text-gray-400 italic">{rule.note}</p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-amber-500 to-teal-600">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-5xl md:text-6xl font-black mb-6">
            Staňte se součástí<br/>Májofestu 2026!
          </h2>
          <p className="text-3xl mb-10 font-black">
            {festivalInfo.edition} 🍺🎸
          </p>
          <Button 
            onClick={scrollToForm}
            size="lg"
            className="bg-teal-900 hover:bg-teal-800 text-amber-400 text-3xl px-20 py-10 font-black rounded-full shadow-2xl hover:shadow-3xl transition-all transform hover:scale-110 border-4 border-white"
          >
            🍺 PŘIHLÁSIT SE NYNÍ 🎸
          </Button>
        </div>
      </section>

      {/* Application Form */}
      {showForm && (
        <section id="application-form" className="py-20 px-4 sm:px-6 lg:px-8 bg-teal-900">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-5xl md:text-6xl font-black mb-4 text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-teal-300">
                📝 Přihláška prodejce
              </h2>
              <p className="text-xl text-gray-300">
                Vyplňte formulář a staňte se součástí festivalu
              </p>
            </div>
            <MajofestForm />
          </div>
        </section>
      )}

      {/* More Events Section */}
      <section className="py-8 sm:py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-teal-800 to-teal-900 border-y-2 sm:border-y-4 border-amber-400">
        <div className="max-w-5xl mx-auto text-center">
          <div className="bg-gradient-to-br from-amber-500/10 to-teal-600/10 rounded-xl sm:rounded-2xl p-6 sm:p-12 border-2 sm:border-4 border-amber-400 shadow-2xl">
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-black mb-3 sm:mb-4 text-amber-400">
              🎪 Máme více festivalů!
            </h2>
            <p className="text-base sm:text-xl text-gray-300 mb-6 sm:mb-8 max-w-2xl mx-auto">
              Zajímá vás prodejní místo i na dalších akcích? Podívejte se na všechny naše festivaly v roce 2026.
            </p>
            <Button 
              onClick={() => navigate('/')}
              className="bg-gradient-to-r from-amber-500 to-teal-600 hover:from-amber-600 hover:to-teal-700 text-white text-base sm:text-xl px-6 sm:px-12 py-4 sm:py-6 font-black rounded-full shadow-2xl hover:shadow-3xl transition-all w-full sm:w-auto"
            >
              🎉 Zobrazit další akce
            </Button>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-teal-900">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-5xl md:text-6xl font-black mb-4 text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-teal-300">
              📞 Kontakt
            </h2>
          </div>

          <Card className="bg-gradient-to-br from-teal-800 to-teal-900 shadow-2xl border-4 border-amber-400">
            <CardContent className="p-8">
              <div className="grid md:grid-cols-3 gap-6 text-center">
                <div>
                  <div className="bg-gradient-to-r from-amber-500 to-teal-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Mail className="h-8 w-8 text-white" />
                  </div>
                  <p className="font-bold text-gray-300 mb-1">Email</p>
                  <a href={`mailto:${contactInfo.email}`} className="text-amber-400 hover:text-amber-500 font-bold text-lg">
                    {contactInfo.email}
                  </a>
                </div>
                <div>
                  <div className="bg-gradient-to-r from-teal-600 to-amber-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Phone className="h-8 w-8 text-white" />
                  </div>
                  <p className="font-bold text-gray-300 mb-1">Telefon</p>
                  <a href={`tel:${contactInfo.phone}`} className="text-amber-400 hover:text-amber-500 font-bold text-lg">
                    {contactInfo.phone}
                  </a>
                </div>
                <div>
                  <div className="bg-gradient-to-r from-amber-500 to-teal-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Users className="h-8 w-8 text-white" />
                  </div>
                  <p className="font-bold text-gray-300 mb-1">Kontaktní osoba</p>
                  <p className="text-white font-bold text-lg">{contactInfo.name}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-r from-amber-500 to-teal-600 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-2xl font-black mb-2">
            🍺 MÁJOFEST OTROKOVICE 2026 🎸
          </p>
          <p className="font-bold">
            © 2026 Májofest | Otrokovice
          </p>
          <p className="text-sm mt-2 font-bold">
            Organizováno společností Vše pro akci
          </p>
        </div>
      </footer>
    </div>
  );
};

export default MajofestLanding;
